import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Check, Sparkles, Crown, Zap, Star, HelpCircle, Smartphone } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Button, CardCorner, TranslatedText, RegistrationWizard } from '../ui';
import { STORE_LINKS } from '../../constants/storeLinks';
import { detectBrowserCountry } from '../../utils/detectCountry';
import { getPaymentCatalog, type PaymentCatalog } from '../../services/api/paymentCatalogService';

interface DisplayPlan {
  key: string;
  icon: LucideIcon;
  tier: string;
  badge: string | null;
  featured: boolean;
  title: string;
  description: string;
  priceText: string;
  durationText: string;
  features: string[];
}

/**
 * BTalentStar club-application plans:
 * Chile: CLP 19.000 / 49.000 / 69.000 / 99.000 — Plan 1 / 3 / 6 / 10 Clubes
 * Other: USD 19 / 49 / 69 / 99 — same tiers
 * Durations: Plan 1 = 12 months; Plan 3/6/10 = 24 months
 */
const PLAN_DEFS = [
  {
    key: 'clubs1',
    clubs: 1,
    durationMonths: 12,
    priceClp: 19000,
    priceUsd: 19,
    icon: Star,
    tier: 'bronze',
    badge: null as string | null,
    featured: false,
    titleKey: 'pricing.planClubs1Title',
    descKey: 'pricing.planClubs1Desc',
    featureKeys: [
      'pricing.planClubs1F1',
      'pricing.planClubs1F2',
      'pricing.planClubs1F3',
      'pricing.planClubs1F4',
      'pricing.planClubs1F5',
      'pricing.planClubs1F6',
    ],
  },
  {
    key: 'clubs3',
    clubs: 3,
    durationMonths: 24,
    priceClp: 49000,
    priceUsd: 49,
    icon: Zap,
    tier: 'silver',
    badge: null as string | null,
    featured: false,
    titleKey: 'pricing.planClubs3Title',
    descKey: 'pricing.planClubs3Desc',
    featureKeys: [
      'pricing.planClubs3F1',
      'pricing.planClubs3F2',
      'pricing.planClubs3F3',
      'pricing.planClubs3F4',
      'pricing.planClubs3F5',
      'pricing.planClubs3F6',
      'pricing.planClubs3F7',
      'pricing.planClubs3F8',
    ],
  },
  {
    key: 'clubs6',
    clubs: 6,
    durationMonths: 24,
    priceClp: 69000,
    priceUsd: 69,
    icon: Crown,
    tier: 'gold',
    badge: 'popular',
    featured: true,
    titleKey: 'pricing.planClubs6Title',
    descKey: 'pricing.planClubs6Desc',
    featureKeys: [
      'pricing.planClubs6F1',
      'pricing.planClubs6F2',
      'pricing.planClubs6F3',
      'pricing.planClubs6F4',
      'pricing.planClubs6F5',
      'pricing.planClubs6F6',
      'pricing.planClubs6F7',
      'pricing.planClubs6F8',
    ],
  },
  {
    key: 'clubs10',
    clubs: 10,
    durationMonths: 24,
    priceClp: 99000,
    priceUsd: 99,
    icon: Sparkles,
    tier: 'platinum',
    badge: null,
    featured: false,
    titleKey: 'pricing.planClubs10Title',
    descKey: 'pricing.planClubs10Desc',
    featureKeys: [
      'pricing.planClubs10F1',
      'pricing.planClubs10F2',
      'pricing.planClubs10F3',
      'pricing.planClubs10F4',
      'pricing.planClubs10F5',
      'pricing.planClubs10F6',
      'pricing.planClubs10F7',
      'pricing.planClubs10F8',
      'pricing.planClubs10F9',
    ],
  },
];

function formatPlanPrice(priceClp: number, priceUsd: number, useClp: boolean): string {
  if (useClp) {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      maximumFractionDigits: 0,
    }).format(priceClp);
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(priceUsd);
}

const tierColors = {
  bronze: {
    bg: 'from-brand-darkNavy/90 to-brand-navy/80',
    border: 'border-white/10 group-hover:border-brand-orange/50',
    accent: 'text-brand-orange',
    glow: 'group-hover:shadow-[0_0_30px_rgba(255,152,0,0.2)]',
  },
  silver: {
    bg: 'from-brand-darkNavy/90 to-brand-navy/80',
    border: 'border-white/10 group-hover:border-brand-teal/50',
    accent: 'text-brand-teal',
    glow: 'group-hover:shadow-[0_0_30px_rgba(0,212,170,0.2)]',
  },
  gold: {
    bg: 'from-brand-navy/90 to-brand-yellow/5',
    border: 'border-brand-yellow/50',
    accent: 'text-brand-yellow',
    glow: 'shadow-glow-yellow hover:shadow-[0_0_50px_rgba(255,215,0,0.4)]',
  },
  platinum: {
    bg: 'from-brand-darkNavy/90 to-brand-navy/80',
    border: 'border-white/10 group-hover:border-brand-cyan/50',
    accent: 'text-brand-cyan',
    glow: 'group-hover:shadow-[0_0_30px_rgba(0,255,255,0.2)]',
  },
};

export function Pricing() {
  const { t } = useTranslation();
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [catalog, setCatalog] = useState<PaymentCatalog | null>(null);

  useEffect(() => {
    getPaymentCatalog(detectBrowserCountry()).then(setCatalog).catch(() => setCatalog(null));
  }, []);

  const country = catalog?.country.code || detectBrowserCountry();
  const useClp = country === 'CL';

  const openRegistration = () => {
    setIsWizardOpen(true);
  };

  const plansToRender: DisplayPlan[] = PLAN_DEFS.map((p) => ({
    key: p.key,
    icon: p.icon,
    tier: p.tier,
    badge: p.badge,
    featured: p.featured,
    title: t(p.titleKey),
    description: t(p.descKey),
    priceText: formatPlanPrice(p.priceClp, p.priceUsd, useClp),
    durationText: `${p.durationMonths} ${t('pricing.months')}`,
    features: p.featureKeys.map((k) => t(k)),
  }));

  return (
    <section id="pricing" className="relative py-24 md:py-32 overflow-hidden bg-transparent">
      <div className="absolute inset-0 bg-brand-darkNavy/50 backdrop-blur-[2px]" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[600px] bg-brand-yellow/5 rounded-full blur-[150px]" />

      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center max-w-3xl mx-auto mb-12"
        >
          <motion.div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-yellow/10 border border-brand-yellow/30 mb-6"
            whileHover={{ scale: 1.05 }}
          >
            <Crown className="w-4 h-4 text-brand-yellow" />
            <span className="text-sm font-bold text-brand-yellow uppercase tracking-wider">Planes</span>
          </motion.div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-6 tracking-tight">
            <TranslatedText i18nKey="pricing.title" type="digital" duration={0.8} />
          </h2>
          <p className="text-lg md:text-xl text-dark-300 mb-4">
            <TranslatedText i18nKey="pricing.subtitle" type="fadeWords" duration={0.4} stagger={0.05} />
          </p>
          <p className="text-dark-400 mb-4">
            <TranslatedText i18nKey="pricing.intro" type="fade" duration={0.5} />
          </p>
          <p className="text-brand-yellow font-semibold">
            <TranslatedText i18nKey="pricing.tagline" type="fade" duration={0.4} />
          </p>
          {catalog?.providers && catalog.providers.length > 0 && (
            <p className="text-sm text-dark-400 mt-3">
              {catalog.country.name}: {catalog.providers.map((p) => p.display_name).join(' · ')}
            </p>
          )}
        </motion.div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto mb-20">
          {plansToRender.map((plan, index) => {
            const colors = tierColors[plan.tier as keyof typeof tierColors];
            const PlanIcon = plan.icon;
            return (
              <motion.div
                key={plan.key}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -10 }}
                className={`relative group ${plan.featured ? 'lg:-mt-4 lg:mb-4' : ''}`}
              >
                <div
                  className={`
                    relative h-full flex flex-col bg-gradient-to-br ${colors.bg} backdrop-blur-xl
                    border ${colors.border} rounded-2xl p-6 overflow-hidden
                    transition-all duration-500 ${colors.glow}
                  `}
                >
                  <CardCorner position="top-left" />
                  <CardCorner position="top-right" />
                  {plan.badge && (
                    <div className="absolute top-4 inset-x-0 flex justify-center z-20">
                      <div className="px-3 py-1 rounded border backdrop-blur-md text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5 shadow-lg bg-brand-yellow/20 border-brand-yellow text-brand-yellow">
                        <Crown className="w-3 h-3" />
                        <TranslatedText i18nKey="pricing.popular" type="fade" />
                      </div>
                    </div>
                  )}
                  <div className="flex justify-center mb-3 pt-6">
                    <motion.div
                      className={`w-14 h-14 rounded-xl bg-brand-navy/50 border border-white/5 flex items-center justify-center ${colors.accent}`}
                      whileHover={{ scale: 1.1, rotate: 10 }}
                      transition={{ type: 'spring', stiffness: 300 }}
                    >
                      <PlanIcon className="w-7 h-7" />
                    </motion.div>
                  </div>
                  <h3 className="text-center text-lg font-bold text-white mb-1">
                    {plan.title}
                  </h3>
                  <div className="text-center mb-4">
                    <span className="text-sm font-bold text-dark-400 uppercase tracking-wider">
                      {plan.durationText}{plan.durationText && plan.priceText ? ' – ' : ''}{plan.priceText}
                    </span>
                  </div>
                  <p className="text-dark-400 text-sm text-center mb-4 min-h-[40px]">
                    {plan.description}
                  </p>
                  <ul className="space-y-2 mb-6 flex-1">
                    {plan.features.map((feature, fi) => (
                      <li key={fi} className="flex items-start gap-2 text-dark-300 text-sm">
                        <Check className={`w-4 h-4 flex-shrink-0 mt-0.5 ${colors.accent}`} />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    variant={plan.featured ? 'primary' : 'outline'}
                    fullWidth
                    className={!plan.featured ? 'border-white/20 text-white hover:bg-white/10 hover:border-white/40' : ''}
                    onClick={openRegistration}
                  >
                    <TranslatedText i18nKey="pricing.subscribe" type="digital" duration={0.5} />
                  </Button>
                  {plan.featured && (
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-tr from-transparent via-brand-yellow/5 to-transparent -skew-x-12 pointer-events-none"
                      animate={{ x: ['-200%', '200%'] }}
                      transition={{ duration: 3, repeat: Infinity, repeatDelay: 5, ease: 'linear' }}
                    />
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* FAQ - ¿Qué significa una postulación? */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-3xl mx-auto mb-20"
        >
          <div className="p-6 md:p-8 rounded-2xl bg-brand-navy/60 border border-brand-lightBlue/20">
            <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <HelpCircle className="w-6 h-6 text-brand-yellow" />
              <TranslatedText i18nKey="pricing.faqTitle" type="fade" />
            </h3>
            <p className="text-dark-300 mb-4">
              <TranslatedText i18nKey="pricing.faqBody" type="fade" />
            </p>
            <ul className="space-y-2 mb-4">
              {['pricing.faqF1', 'pricing.faqF2', 'pricing.faqF3', 'pricing.faqF4', 'pricing.faqF5'].map((k) => (
                <li key={k} className="flex items-start gap-2 text-dark-400 text-sm">
                  <Check className="w-4 h-4 text-brand-yellow flex-shrink-0 mt-0.5" />
                  <TranslatedText i18nKey={k} type="fade" />
                </li>
              ))}
            </ul>
            <p className="text-brand-yellow/90 font-medium">
              <TranslatedText i18nKey="pricing.faqClosing" type="fade" />
            </p>
          </div>
        </motion.div>

        {/* CTA - Listo para comenzar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center p-8 md:p-10 rounded-3xl bg-gradient-to-br from-brand-yellow/10 to-amber-500/5 border-2 border-brand-yellow/30"
        >
          <h3 className="text-2xl md:text-3xl font-black text-white mb-4">
            <TranslatedText i18nKey="pricing.ctaTitle" type="fade" duration={0.5} />
          </h3>
          <p className="text-dark-300 max-w-2xl mx-auto mb-6">
            <TranslatedText i18nKey="pricing.ctaSubtitle" type="fade" duration={0.4} />
          </p>
          <ul className="flex flex-wrap justify-center gap-x-6 gap-y-2 mb-6 text-sm text-dark-300">
            <li className="flex items-center gap-2">
              <Smartphone className="w-4 h-4 text-brand-yellow" />
              <TranslatedText i18nKey="pricing.ctaDownload" type="fade" />
            </li>
            <li className="flex items-center gap-2">
              <Check className="w-4 h-4 text-brand-yellow" />
              <TranslatedText i18nKey="pricing.ctaAvailable" type="fade" />
            </li>
            <li className="flex items-center gap-2">
              <Check className="w-4 h-4 text-brand-yellow" />
              <TranslatedText i18nKey="pricing.ctaAccess" type="fade" />
            </li>
          </ul>
          <div className="flex flex-wrap justify-center gap-4">
            <Button variant="primary" size="lg" onClick={openRegistration}>
              <TranslatedText i18nKey="pricing.ctaButton" type="fade" />
            </Button>
            <div className="flex flex-wrap justify-center gap-3 text-sm">
              <a
                href={STORE_LINKS.appStore}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 rounded-xl bg-white/10 border border-white/20 text-white hover:bg-white/20 transition-colors"
              >
                <TranslatedText i18nKey="pricing.appStore" type="fade" />
              </a>
              <a
                href={STORE_LINKS.googlePlay}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 rounded-xl bg-white/10 border border-white/20 text-white hover:bg-white/20 transition-colors"
              >
                <TranslatedText i18nKey="pricing.googlePlay" type="fade" />
              </a>
              <a
                href={STORE_LINKS.appGallery}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 rounded-xl bg-white/10 border border-white/20 text-white hover:bg-white/20 transition-colors"
              >
                AppGallery
              </a>
            </div>
          </div>
        </motion.div>
      </div>

      <RegistrationWizard
        isOpen={isWizardOpen}
        onClose={() => setIsWizardOpen(false)}
      />
    </section>
  );
}
